import React, { useEffect, useState } from "react";

/**
 * AnimatedEnergyOverlay
 * Premium 3D isometric animated data-packet and energy network overlay.
 * Perfectly overlays on top of /images/opportunities/hero.png (1538 x 962).
 * Preserves the original image 100% pixel-for-pixel unchanged underneath.
 */

// SVG path definitions strictly and accurately mapped ONLY to visible circuit tracks originating from central cube
const CIRCUIT_TRACKS = [
  // 1. Central Hub -> Revenue & Growth (Top - Direct Vertical Stem)
  {
    id: "track-revenue-growth",
    d: "M 700 330 L 700 278",
    targetNode: { cx: 700, cy: 278 },
  },

  // 2. Central Hub -> Customer Experience (Top-Left Spoke through junction node into platform)
  {
    id: "track-cx-spoke",
    d: "M 600 379 L 455 250 L 345 315",
    targetNode: { cx: 345, cy: 315 },
  },

  // 3. Central Hub -> Operations (Top-Right Spoke through junction node into platform)
  {
    id: "track-ops-spoke",
    d: "M 808 369 L 941 243 L 1060 315",
    targetNode: { cx: 1060, cy: 315 },
  },

  // 4. Central Hub -> People (Middle-Left Spoke into platform card)
  {
    id: "track-people-spoke",
    d: "M 546 516 L 456 512 Q 400 518 345 524",
    targetNode: { cx: 345, cy: 524 },
  },

  // 5. Central Hub -> Finance (Middle-Right Spoke into Finance card/platform)
  {
    id: "track-finance-spoke",
    d: "M 850 518 L 946 512 Q 1010 520 1080 528",
    targetNode: { cx: 1080, cy: 528 },
  },

  // 6. Central Hub -> Technology (Bottom-Left Spoke to platform)
  {
    id: "track-tech-spoke",
    d: "M 559 583 L 526 605 L 500 625",
    targetNode: { cx: 500, cy: 625 },
  },

  // 7. Central Hub -> Knowledge & Decisions (Bottom-Right Spoke to platform)
  {
    id: "track-kd-spoke",
    d: "M 840 560 L 875 605 L 900 625",
    targetNode: { cx: 900, cy: 625 },
  },

  // 8. Central Hub -> Bottom Center Spine (Cube bottom vertex through inner node to bottom outer node)
  {
    id: "track-bottom-spine",
    d: "M 698 603 L 698 652 L 697 719",
    targetNode: { cx: 697, cy: 719 },
  },
];

// Terminal nodes & major junction dots on the actual visible artwork
const TERMINAL_NODES = [
  { cx: 700, cy: 278, r: 6, label: "Revenue & Growth" },
  { cx: 700, cy: 305, r: 5, label: "Top Stem Node" },
  { cx: 455, cy: 250, r: 8, label: "CX Upper Junction" },
  { cx: 345, cy: 315, r: 5, label: "Customer Experience" },
  { cx: 941, cy: 243, r: 8, label: "Ops Upper Junction" },
  { cx: 1060, cy: 315, r: 5, label: "Operations" },
  { cx: 456, cy: 512, r: 8, label: "People Junction" },
  { cx: 345, cy: 524, r: 5, label: "People" },
  { cx: 946, cy: 512, r: 8, label: "Finance Junction" },
  { cx: 1080, cy: 528, r: 6, label: "Finance" },
  { cx: 526, cy: 605, r: 7, label: "Tech Junction" },
  { cx: 500, cy: 625, r: 5, label: "Technology" },
  { cx: 875, cy: 605, r: 7, label: "KD Junction" },
  { cx: 900, cy: 625, r: 5, label: "Knowledge" },
  { cx: 698, cy: 652, r: 6, label: "Bottom Inner Node" },
  { cx: 697, cy: 719, r: 8, label: "Bottom Hub Node" },
  { cx: 600, cy: 379, r: 6, label: "Cube Top-Left Corner" },
  { cx: 808, cy: 369, r: 6, label: "Cube Top-Right Corner" },
  { cx: 698, cy: 603, r: 6, label: "Cube Bottom Vertex" },
];

export const AnimatedEnergyOverlay = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none select-none z-10"
      viewBox="0 0 1538 962"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <defs>
        {/* Neon cyan glow filters */}
        <filter id="energyGlow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="5.5" result="blur2" />
          <feMerge>
            <feMergeNode in="blur2" />
            <feMergeNode in="blur1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <filter id="softGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Soft light blue / cyan luminous glow filter for moving energy points */}
        <filter id="pointAuraGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4.0" result="softBlur" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.8" result="sharpBlur" />
          <feMerge>
            <feMergeNode in="softBlur" />
            <feMergeNode in="sharpBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Central hub radial glow */}
        <radialGradient id="centralPulseGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.45" />
          <stop offset="45%" stopColor="#0077FE" stopOpacity="0.22" />
          <stop offset="80%" stopColor="#023068" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#023068" stopOpacity="0" />
        </radialGradient>

        {/* Subtle platform rim gradient */}
        <linearGradient id="hubRimGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.8" />
          <stop offset="50%" stopColor="#38BDF8" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#1E40AF" stopOpacity="0.2" />
        </linearGradient>

        {/* Outer terminal pulse gradient */}
        <radialGradient id="terminalGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.85" />
          <stop offset="35%" stopColor="#00F0FF" stopOpacity="0.75" />
          <stop offset="75%" stopColor="#0284C7" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#0284C7" stopOpacity="0" />
        </radialGradient>

        {/* Energy trail packet gradient */}
        <linearGradient id="trailGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00D2EE" stopOpacity="0" />
          <stop offset="70%" stopColor="#00D2EE" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.95" />
        </linearGradient>
      </defs>

      {/* ========================================================
          1. CENTRAL "YOUR BUSINESS" HUB GLOW ANIMATION
          Subtle, elegant pulse (3.6s cycle) that enhances existing cube
          without moving it or altering the readable text.
          ======================================================== */}
      <g className="central-hub-glow">
        {/* Soft breathing radial background aura beneath cube */}
        <ellipse
          cx="700"
          cy="485"
          rx="170"
          ry="96"
          fill="url(#centralPulseGrad)"
        >
          {!prefersReducedMotion && (
            <>
              <animate
                attributeName="opacity"
                values="0.35;0.88;0.48;0.35;0.35"
                dur="3.2s"
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;0.12;0.30;0.375;1"
                keySplines="0.25 0.1 0.25 1; 0.25 0.1 0.25 1; 0.25 0.1 0.25 1; 0.25 0.1 0.25 1"
              />
              <animate
                attributeName="rx"
                values="165;190;174;165;165"
                dur="3.2s"
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;0.12;0.30;0.375;1"
                keySplines="0.25 0.1 0.25 1; 0.25 0.1 0.25 1; 0.25 0.1 0.25 1; 0.25 0.1 0.25 1"
              />
              <animate
                attributeName="ry"
                values="92;108;97;92;92"
                dur="3.2s"
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;0.12;0.30;0.375;1"
                keySplines="0.25 0.1 0.25 1; 0.25 0.1 0.25 1; 0.25 0.1 0.25 1; 0.25 0.1 0.25 1"
              />
            </>
          )}
        </ellipse>
      </g>

      {/* ========================================================
          2. ANIMATED CIRCUIT PACKETS (PRIMARY FEATURE)
          White core with light blue/cyan neon glow.
          Travels smoothly and fast (1.2s travel), then pauses for 2.0s
          before the next wave launches (total cycle = 3.2s).
          ======================================================== */}
      {!prefersReducedMotion && (
        <g className="circuit-pulses">
          {CIRCUIT_TRACKS.map((track) => (
            <g key={track.id}>
              {/* Invisible reference path for SVG animateMotion */}
              <path
                id={`path-${track.id}`}
                d={track.d}
                fill="none"
                stroke="none"
              />

              {/* Synchronized single-point wave */}
              <g id={`${track.id}-pulse`}>
                {/* 1. Outer soft light blue / cyan glowing aura */}
                <circle
                  r="7.5"
                  fill="#38BDF8"
                  opacity="0"
                  filter="url(#pointAuraGlow)"
                >
                  <animateMotion
                    dur="3.2s"
                    repeatCount="indefinite"
                    rotate="auto"
                    keyPoints="0;1;1"
                    keyTimes="0;0.375;1"
                    calcMode="spline"
                    keySplines="0.25 0.1 0.25 1; 0 0 1 1"
                  >
                    <mpath href={`#path-${track.id}`} />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;0.75;0.75;0;0;0"
                    keyTimes="0;0.03;0.35;0.375;0.98;1"
                    dur="3.2s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="r"
                    values="4.5;7.5;7.5;4.5;4.5;4.5"
                    keyTimes="0;0.08;0.33;0.375;0.98;1"
                    dur="3.2s"
                    repeatCount="indefinite"
                  />
                </circle>

                {/* 2. Mid bright sky blue / cyan halo */}
                <circle
                  r="4.8"
                  fill="#7DD3FC"
                  opacity="0"
                  filter="url(#pointAuraGlow)"
                >
                  <animateMotion
                    dur="3.2s"
                    repeatCount="indefinite"
                    rotate="auto"
                    keyPoints="0;1;1"
                    keyTimes="0;0.375;1"
                    calcMode="spline"
                    keySplines="0.25 0.1 0.25 1; 0 0 1 1"
                  >
                    <mpath href={`#path-${track.id}`} />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;0.9;0.9;0;0;0"
                    keyTimes="0;0.03;0.35;0.375;0.98;1"
                    dur="3.2s"
                    repeatCount="indefinite"
                  />
                </circle>

                {/* 3. Incandescent pure white core */}
                <circle
                  r="2.8"
                  fill="#FFFFFF"
                  opacity="0"
                  filter="url(#energyGlow)"
                >
                  <animateMotion
                    dur="3.2s"
                    repeatCount="indefinite"
                    rotate="auto"
                    keyPoints="0;1;1"
                    keyTimes="0;0.375;1"
                    calcMode="spline"
                    keySplines="0.25 0.1 0.25 1; 0 0 1 1"
                  >
                    <mpath href={`#path-${track.id}`} />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;1;1;0;0;0"
                    keyTimes="0;0.03;0.35;0.375;0.98;1"
                    dur="3.2s"
                    repeatCount="indefinite"
                  />
                </circle>

                {/* 4. Ultra-bright pinpoint center */}
                <circle
                  r="1.6"
                  fill="#FFFFFF"
                  opacity="0"
                >
                  <animateMotion
                    dur="3.2s"
                    repeatCount="indefinite"
                    rotate="auto"
                    keyPoints="0;1;1"
                    keyTimes="0;0.375;1"
                    calcMode="spline"
                    keySplines="0.25 0.1 0.25 1; 0 0 1 1"
                  >
                    <mpath href={`#path-${track.id}`} />
                  </animateMotion>
                  <animate
                    attributeName="opacity"
                    values="0;1;1;0;0;0"
                    keyTimes="0;0.03;0.35;0.375;0.98;1"
                    dur="3.2s"
                    repeatCount="indefinite"
                  />
                </circle>
              </g>
            </g>
          ))}
        </g>
      )}

      {/* ========================================================
          3. SUBTLE LIGHTING ON OUTER PLATFORMS & JUNCTION NODES
          Gentle shimmer that reacts when packets arrive at 1.2s,
          then rests calmly during the 2.0s pause.
          ======================================================== */}
      <g className="terminal-arrival-shimmer">
        {TERMINAL_NODES.map((node, i) => (
          <g key={`terminal-${i}`}>
            <circle
              cx={node.cx}
              cy={node.cy}
              r={node.r}
              fill="url(#terminalGrad)"
              filter="url(#softGlow)"
              opacity="0.25"
            >
              {!prefersReducedMotion && (
                <animate
                  attributeName="opacity"
                  values="0.25;0.25;0.95;0.25;0.25"
                  dur="3.2s"
                  repeatCount="indefinite"
                  calcMode="spline"
                  keyTimes="0;0.34;0.40;0.52;1"
                  keySplines="0.25 0.1 0.25 1; 0.25 0.1 0.25 1; 0.25 0.1 0.25 1; 0.25 0.1 0.25 1"
                />
              )}
            </circle>

            {/* Micro pin-point highlight */}
            <circle
              cx={node.cx}
              cy={node.cy}
              r={node.r * 0.38}
              fill="#FFFFFF"
              opacity="0.5"
            >
              {!prefersReducedMotion && (
                <animate
                  attributeName="opacity"
                  values="0.25;0.25;1;0.25;0.25"
                  dur="3.2s"
                  repeatCount="indefinite"
                  keyTimes="0;0.34;0.40;0.52;1"
                />
              )}
            </circle>
          </g>
        ))}
      </g>
    </svg>
  );
};

export default AnimatedEnergyOverlay;
